import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, X } from "lucide-react";

export default function AdminCategories() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = () => api.get("/categories").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    async function remove(c) {
        if (!window.confirm(`Eliminar a categoria "${c.name}"?`)) return;
        try {
            await api.delete(`/admin/categories/${c.id}`);
            toast.success("Categoria eliminada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao eliminar");
        }
    }

    return (
        <section className="space-y-6" data-testid="admin-categories">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <span className="label-overline text-wine/60">Catálogo</span>
                    <h1 className="font-heading text-3xl text-wine-dark">Categorias</h1>
                    <p className="text-sm text-wine-dark/60 mt-1">{list.length} categorias ativas no marketplace</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-wine text-xs px-5 py-2.5 inline-flex items-center gap-2" data-testid="add-category">
                    <Plus className="h-4 w-4" /> Nova categoria
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((c) => (
                    <div key={c.id} className="bg-white rounded-md border border-gold-soft/30 p-5 shadow-sm" data-testid={`category-card-${c.slug}`}>
                        <div className="flex items-start justify-between">
                            <div className="w-10 h-10 rounded-md bg-wine/10 flex items-center justify-center text-wine">
                                <Tag className="h-5 w-5" strokeWidth={1.5} />
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 hover:bg-cream rounded-md text-wine-dark/60 hover:text-wine" data-testid={`edit-cat-${c.slug}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => remove(c)} className="p-2 hover:bg-destructive/10 rounded-md text-wine-dark/60 hover:text-destructive" data-testid={`delete-cat-${c.slug}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                        <h3 className="font-heading text-xl text-wine-dark mt-4">{c.name}</h3>
                        <p className="text-xs text-wine-dark/55 mt-1 line-clamp-2">{c.description || "—"}</p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-wine-dark/5 text-xs">
                            <span className="text-wine-dark/50">slug: <code className="text-wine">{c.slug}</code></span>
                            <span className="bg-gold-soft/20 text-gold-deep px-2 py-0.5 rounded-sm font-semibold">{c.products_count} prod.</span>
                        </div>
                    </div>
                ))}
                {list.length === 0 && (
                    <p className="col-span-full text-center py-10 text-wine-dark/60">Sem categorias.</p>
                )}
            </div>

            {showForm && (
                <CategoryForm
                    initial={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); load(); }}
                />
            )}
        </section>
    );
}

function CategoryForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: initial?.name || "",
        slug: initial?.slug || "",
        description: initial?.description || "",
        icon: initial?.icon || "tag",
        order: initial?.order || 100,
    });
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            if (initial) {
                await api.patch(`/admin/categories/${initial.id}`, form);
                toast.success("Categoria atualizada");
            } else {
                await api.post("/admin/categories", form);
                toast.success("Categoria criada");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-elegant/40 backdrop-blur-sm p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-cream w-full max-w-md rounded-md p-8 space-y-4" data-testid="category-form">
                <div className="flex items-start justify-between">
                    <div>
                        <span className="label-overline text-wine">{initial ? "Editar" : "Nova"}</span>
                        <h2 className="font-heading text-2xl text-wine-dark mt-1">Categoria</h2>
                    </div>
                    <button type="button" onClick={onClose} className="text-wine-dark/60 hover:text-wine">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <Field label="Nome" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="cat-name" />
                <Field label="Slug (opcional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="cat-slug" />
                <Field label="Descrição" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea testId="cat-desc" />
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Ícone (lucide)" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} testId="cat-icon" />
                    <Field label="Ordem" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} testId="cat-order" />
                </div>

                <button type="submit" disabled={saving} className="btn-wine w-full" data-testid="save-category">
                    {saving ? "A guardar…" : initial ? "Atualizar" : "Criar"}
                </button>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", textarea, required, testId }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/60 mb-1.5 block">{label}</span>
            {textarea ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                    data-testid={testId}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    required={required}
                    className="w-full px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm focus:border-wine focus:outline-none"
                    data-testid={testId}
                />
            )}
        </label>
    );
}
